// Above 1000 results `total` is only the planner's estimate, so it cannot tell
// whether another page exists: the server reads one row more than a page and
// answers `has_more`. The count in the header never falls below the rows
// already on the list, and once the last page is read it is exact.
// A response without `has_more` (an older server) falls back to the total and
// takes an empty page as the end of the list.
export function resolvePageCount(data, pageRowCount, loadedRowCount) {
    var total = Number(data && data.total) || 0;
    var estimated = !!(data && data.total_estimated === true);
    var known = !!data && typeof data.has_more === 'boolean';
    var hasMore = known ? data.has_more : (loadedRowCount < total && pageRowCount > 0);

    if (hasMore) {
        total = Math.max(total, loadedRowCount + 1);
    } else if (estimated && (known || pageRowCount === 0)) {
        total = loadedRowCount;
        estimated = false;
    }

    return {
        total: Math.max(total, loadedRowCount),
        estimated: estimated,
        hasMore: hasMore,
    };
}

var LIST_MORE_RETRY_LABEL = 'Spróbuj ponownie';
var LIST_MORE_ERROR = 'Nie udało się wczytać kolejnych transakcji.';

// The footer under the transaction list: „Pokaż kolejne transakcje” and the
// error of a failed next page. The rows already loaded stay on the list; the
// error stands just above the button, which retries the page that failed.
export function renderListMoreControls(button, errorEl, view) {
    if (!button) return;
    var failed = !!(view.visible && view.failed);
    button.style.display = view.visible ? '' : 'none';
    button.textContent = failed ? LIST_MORE_RETRY_LABEL : view.label;
    if (errorEl) {
        errorEl.hidden = !failed;
        errorEl.textContent = failed ? LIST_MORE_ERROR : '';
    }
}

export function createListDataManager(config) {
    var state = config.state;

    function getListQuery(page) {
        return config.qstr(Object.assign({
            bbox: config.currentBbox(0),
            page: page,
            zoom: config.mapZoom(),
            per_page: config.LIST_PER_PAGE,
            sort: config.sortField(state.getCurrentSort()),
            order: config.sortDir(state.getCurrentSort())
        }, config.getApiFilters()));
    }

    function hydrateListRows(rows, append) {
        var rowCoords = append ? (state.getRowCoords() || {}) : {};
        rows.forEach(function(row) {
            if (row.lat != null && row.lng != null) {
                rowCoords[row.tran_lokalny_id_iip] = { lat: parseFloat(row.lat), lng: parseFloat(row.lng) };
            }
        });
        state.setRowCoords(rowCoords);
    }

    function parseResponse(response) {
        return response.json().catch(function() { return {}; }).then(function(payload) {
            if (response.ok) return payload;
            var error = new Error(
                payload && payload.error && payload.error.message
                    ? payload.error.message
                    : 'Błąd ładowania listy'
            );
            error.status = response.status;
            error.payload = payload;
            throw error;
        });
    }

    function loadListPage(page, append) {
        var previousRequest = state.getAbortList();
        if (previousRequest) previousRequest.abort();
        var version = state.bumpListRequestVersion();
        state.setListError(null);
        state.setListMoreFailed(false);
        if (config.mapZoom() < config.INDIVIDUAL_ZOOM_THRESHOLD) {
            state.setListRows([]);
            state.setListTotal(0);
            state.setListTotalEstimated(false);
            state.setListHasMore(false);
            state.setListLoading(false);
            config.renderListPanel();
            return;
        }

        var abortList = new AbortController();
        state.setAbortList(abortList);

        state.setListLoading(true);
        config.renderListPanel();

        fetch('api/list?' + getListQuery(page), { signal: abortList.signal })
            .then(parseResponse)
            .then(function(data) {
                if (version !== state.getListRequestVersion()) return;

                var rows = Array.isArray(data.rows) ? data.rows : [];
                hydrateListRows(rows, append);

                var nextRows = append ? state.getListRows().concat(rows) : rows;
                state.setListRows(nextRows);
                state.setListPage(page);

                var pageCount = resolvePageCount(data, rows.length, nextRows.length);
                state.setListTotalEstimated(pageCount.estimated);
                state.setListTotal(pageCount.total);
                state.setListHasMore(pageCount.hasMore);
                state.setListLoading(false);

                var selectedTid = state.getSelectedTid();
                if (selectedTid) {
                    var match = nextRows.find(function(row) {
                        return row.tran_lokalny_id_iip === selectedTid;
                    });
                    if (match) {
                        state.setSelectedTransactionRow(match);
                        if (!state.getSelectedLatLng()) {
                            var rowCoords = state.getRowCoords();
                            if (rowCoords[selectedTid]) {
                                state.setSelectedLatLng({
                                    lat: rowCoords[selectedTid].lat,
                                    lng: rowCoords[selectedTid].lng,
                                });
                            }
                        }
                    }
                }

                config.renderListPanel();
                if (selectedTid) config.highlightTransactionById(selectedTid);
                if (state.getPendingNearbyRestore() && state.getSelectedLatLng() && !state.getNearbyLoaded()) {
                    config.loadNearbyForSelection(true);
                }
            })
            .catch(function(err) {
                if (err.name === 'AbortError') return;
                if (version !== state.getListRequestVersion()) return;
                state.setListLoading(false);
                // A failed next page keeps the rows and the page counter, so
                // „Pokaż kolejne transakcje” asks for the same page again.
                if (append && state.getListRows().length) {
                    state.setListMoreFailed(true);
                } else {
                    state.setListError(err.message || 'Spróbuj ponownie.');
                }
                config.renderListPanel();
            });
    }

    return {
        loadListPage: loadListPage,
    };
}
